import { isNonEmpty } from '@sgme/fp';
import { DigitInput } from 'components/shared/digit-input';
import { TenorFieldData, TenorFieldUnitOption, TenorFieldValue } from 'components/shared/TenorField/TenorField';
import { useTenorUnits } from 'hooks/use-tenor-units';
import { useTranslateForm } from 'hooks/use-translate-payoff';
import { CustomizableTenorFieldMetadata } from 'models/forms/fields';
import { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { ErrorWrapper } from 'routes/essential-workspace/components/form/error-wrapper';
import { DEFAULT_INCREMENT_STEP } from 'routes/essential-workspace/components/form/fields/digit';
import { getUnitOptions, Unit } from 'routes/essential-workspace/components/form/fields/utils';
import { useFormErrors } from 'routes/essential-workspace/components/form/use-form-errors';
import { useFormFieldData } from 'routes/essential-workspace/components/form/use-form-field-data';
import { getTenorFieldUnitLabel } from 'utils/tenor/units';

import { CustomTenorField } from './custom';

export function TenorField({ id, isReadOnly, translationKey, ...fieldMetadata }: CustomizableTenorFieldMetadata) {
  const intl = useIntl();

  const { getFieldError } = useFormErrors();
  const fieldError = getFieldError(id);

  const { translateLabel } = useTranslateForm();
  const label = translateLabel(translationKey ?? id);

  const [formDataValue, setFormData] = useFormFieldData<TenorFieldData>(id);

  const availableUnits: TenorFieldUnitOption[] = (fieldMetadata?.availableUnits || []).map((unit: string) => ({
    label: intl.formatMessage({ id: `form.${translationKey}.option.${unit}` as any }),
    shortcut: unit.charAt(0).toLowerCase(),
    value: unit,
  }));

  const defaultTenorValue: TenorFieldValue = {
    value: 0,
    unit: isNonEmpty(availableUnits) ? availableUnits[0].value : '%',
  };
  const tenorValue: TenorFieldValue = formDataValue?.currentValue ?? defaultTenorValue;

  const { onInputKeyPress, unit, setUnit } = useTenorUnits({ initialUnit: tenorValue.unit, availableUnits });

  const valuePresets = getUnitOptions(unit.toLowerCase() as Unit);

  useEffect(() => {
    setUnit(tenorValue.unit);
  }, [tenorValue, setUnit]);

  const handleOnValueChanged = useCallback(
    (newValue: Nullable<number>) => {
      if (tenorValue.value !== newValue || tenorValue.unit !== unit) {
        setFormData({
          currentValue: {
            value: newValue ?? undefined,
            unit,
          },
          isCustomized: formDataValue ? formDataValue.isCustomized : false,
        });
      }
    },
    [tenorValue.unit, tenorValue.value, setFormData, formDataValue, unit],
  );

  const handleAddOrRemove = useCallback(
    (action: 'add' | 'remove') => {
      const delta = action === 'add' ? DEFAULT_INCREMENT_STEP : -DEFAULT_INCREMENT_STEP;
      const newValue = (tenorValue.value ?? 0) + delta;
      setFormData({
        currentValue: {
          value: newValue,
          unit: tenorValue.unit,
        },
        isCustomized: formDataValue ? formDataValue.isCustomized : false,
      });
    },
    [tenorValue.value, tenorValue.unit, setFormData, formDataValue],
  );

  const isCustomizedValue = formDataValue?.isCustomized ?? false;

  const removeCustomValue = () => {
    setFormData({ currentValue: tenorValue, isCustomized: false });
  };

  return (
    <ErrorWrapper error={fieldError}>
      {isCustomizedValue ? (
        <CustomTenorField id={id} label={label} onClose={removeCustomValue} />
      ) : (
        <DigitInput
          id={id}
          label={label}
          unit={getTenorFieldUnitLabel(availableUnits, unit)}
          value={tenorValue.value ?? null}
          valuePresets={valuePresets}
          onValueChanged={handleOnValueChanged}
          onAddOrRemove={handleAddOrRemove}
          onKeyPress={onInputKeyPress}
          incrementStep={DEFAULT_INCREMENT_STEP}
          isReadOnly={isReadOnly}
          isInvalid={fieldError?.severity === 'Error'}
          highlightColor={fieldError?.severity === 'Warning' ? 'info' : undefined}
        />
      )}
    </ErrorWrapper>
  );
}
